/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { motion } from "motion/react";
import { Suspense } from "react";
import BackButton from "./BackButton";
import { useLanguage } from "../context/LanguageContext";

interface PageWrapperProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  backTo?: string;
  backLabel?: string;
  fallback?: React.ReactNode;
  className?: string;
}

export default function PageWrapper({
  children,
  title,
  subtitle,
  backTo = "/",
  backLabel,
  fallback,
  className = "",
}: PageWrapperProps) {
  const { isRtl, t } = useLanguage();

  const defaultFallback = (
    <div className="w-full min-h-[400px] flex items-center justify-center border-y border-amber-500/5 bg-[#080604]/40">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <span className="text-xs text-amber-500/60 font-mono tracking-widest uppercase">
          {isRtl ? "در حال بارگذاری..." : "Loading..."}
        </span>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className={`pt-4 ${className}`}
    >
      {/* Back Button / Navigation Breadcrumb */}
      <div className="max-w-6xl mx-auto px-4 mt-6">
        <BackButton to={backTo} label={backLabel} />
      </div>

      {/* Section Content */}
      <Suspense fallback={fallback || defaultFallback}>
        {children}
      </Suspense>
    </motion.div>
  );
}